import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  fetchFeaturedCmsNews,
  normalizeCmsPostsForLegacyView,
} from "../../api/cmsClient";
import { getCmsLocaleFromLanguage } from "../../api/cmsLocale";
import {
  cacheCmsFeaturedPosts,
  fetchLocalNews,
  getCachedCmsFeaturedPosts,
} from "../../api/newsFallback";

const featuredNewsLimit = 1;

function slugify(value = "") {
  return String(value || "")
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/đ/gi, "dj")
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/(^-|-$)+/g, "");
}

function getNewsSlug(post) {
  return post?.attributes?.Slug || slugify(post?.attributes?.Title || "novost");
}

function getNewsPath(post) {
  if (post?.routePath) return post.routePath;
  return `/novosti/${post.id}-${getNewsSlug(post)}`;
}

function getPostImage(post) {
  return post?.attributes?.Image || "/assets/img/logo/fpepblog.png";
}

function getPostExcerpt(post, maxLength = 235) {
  const description = post?.attributes?.Description || "";
  const normalized = String(description).replace(/\s+/g, " ").trim();

  if (normalized.length <= maxLength) return normalized;
  return `${normalized.substring(0, maxLength).trim()}...`;
}

function FeaturedNewsSkeleton() {
  return (
    <section className="fpep-featured-news-area pt-40 pb-50" aria-hidden="true">
      <div className="container">
        <div className="fpep-featured-news fpep-featured-news--loading">
          <div className="fpep-featured-news__image" />
          <div className="fpep-featured-news__content">
            <span className="fpep-featured-news__line fpep-featured-news__line--badge" />
            <span className="fpep-featured-news__line fpep-featured-news__line--title" />
            <span className="fpep-featured-news__line" />
            <span className="fpep-featured-news__line" />
            <span className="fpep-featured-news__line fpep-featured-news__line--button" />
          </div>
        </div>
      </div>
    </section>
  );
}

function FeaturedNews() {
  const { t, i18n } = useTranslation();
  const [post, setPost] = useState(null);
  const [loading, setLoading] = useState(true);
  const cmsLocale = getCmsLocaleFromLanguage(i18n.language);
  const isEnglish = cmsLocale === "en";

  useEffect(() => {
    let isMounted = true;
    const controller = new AbortController();

    const applyPost = (items = []) => {
      if (!isMounted) return;
      setPost(items[0] || null);
    };

    const loadLocalFallback = async () => {
      const localPosts = await fetchLocalNews();
      applyPost(localPosts.slice(0, featuredNewsLimit));
    };

    const loadFeaturedNews = async () => {
      setLoading(true);

      try {
        const result = await fetchFeaturedCmsNews({
          limit: featuredNewsLimit,
          locale: cmsLocale,
          signal: controller.signal,
        });
        const featuredPosts = normalizeCmsPostsForLegacyView(result.data || []);

        if (featuredPosts.length) {
          cacheCmsFeaturedPosts(featuredNewsLimit, featuredPosts, cmsLocale);
          applyPost(featuredPosts);
          return;
        }

        const cachedPosts = getCachedCmsFeaturedPosts(featuredNewsLimit, cmsLocale);
        if (cachedPosts.length) {
          applyPost(cachedPosts);
          return;
        }

        await loadLocalFallback();
      } catch (error) {
        if (controller.signal.aborted || error?.name === "AbortError") return;

        const cachedPosts = getCachedCmsFeaturedPosts(featuredNewsLimit, cmsLocale);
        if (cachedPosts.length) {
          applyPost(cachedPosts);
          return;
        }

        try {
          await loadLocalFallback();
        } catch (fallbackError) {
          console.warn("Homepage featured news fallback failed.", fallbackError);
          applyPost([]);
        }
      } finally {
        if (isMounted) setLoading(false);
      }
    };

    loadFeaturedNews();

    return () => {
      isMounted = false;
      controller.abort();
    };
  }, [cmsLocale]);

  if (loading) {
    return <FeaturedNewsSkeleton />;
  }

  if (!post) {
    return null;
  }

  const postPath = getNewsPath(post);
  const title = post.attributes?.Title || (isEnglish ? "Featured news" : "Izdvojena objava");

  return (
    <section className="fpep-featured-news-area pt-40 pb-50 p-relative fix">
      <div className="container">
        <article className="fpep-featured-news">
          <Link to={postPath} className="fpep-featured-news__image" aria-label={title}>
            <img
              src={getPostImage(post)}
              alt={title}
              loading="lazy"
              onError={(event) => {
                event.currentTarget.src = "/assets/img/logo/fpepblog.png";
              }}
            />
          </Link>

          <div className="fpep-featured-news__content">
            <div className="fpep-featured-news__meta">
              <span className="fpep-featured-news__badge">
                <i className="fas fa-star" /> {isEnglish ? "Featured" : "Izdvojeno"}
              </span>
              {post.attributes?.Date && (
                <span className="fpep-featured-news__date">
                  <i className="fas fa-calendar-alt" /> {post.attributes.Date}
                </span>
              )}
            </div>

            <h2>
              <Link to={postPath}>{title}</Link>
            </h2>

            <p>{getPostExcerpt(post)}</p>

            <div className="fpep-featured-news__actions">
              <Link to={postPath} className="btn ss-btn fpep-featured-news__primary">
                {t("header.SaznajVise")} <i className="fal fa-long-arrow-right" />
              </Link>
              <Link to="/novosti" className="fpep-featured-news__secondary">
                {isEnglish ? "All news" : "Sve novosti"}
              </Link>
            </div>
          </div>
        </article>
      </div>
    </section>
  );
}

export default FeaturedNews;
